import { useMemo, useState, type ReactElement } from 'react';
import { usePeriodCompare, type PeriodTotals } from '@/lib/analytics';

/**
 * Tab "Comparativa de periodos".
 *
 * Tres presets de comparación:
 *   - Este mes vs mes-1
 *   - Este año vs año-1
 *   - Última semana vs semana-1
 *
 * Tabla con las métricas y el delta porcentual (Δ%) entre periodos.
 */

type PresetId = 'month' | 'year' | 'week';

const PRESETS: Array<{ id: PresetId; label: string }> = [
  { id: 'month', label: 'Este mes vs mes anterior' },
  { id: 'year', label: 'Este año vs año anterior' },
  { id: 'week', label: 'Última semana vs semana anterior' },
];

interface RangePair {
  a: { fromISO: string; toISO: string; label: string };
  b: { fromISO: string; toISO: string; label: string };
}

export function PeriodCompare(): ReactElement {
  const [preset, setPreset] = useState<PresetId>('month');
  const ranges: RangePair = useMemo(() => buildRanges(preset), [preset]);
  const query = usePeriodCompare(ranges.a, ranges.b, true);

  return (
    <article className="space-y-4">
      <header className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPreset(p.id)}
            aria-pressed={preset === p.id}
            className={
              preset === p.id
                ? 'rounded-md bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1'
                : 'rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1'
            }
          >
            {p.label}
          </button>
        ))}
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-sm text-slate-600">
          Cargando comparativa…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="text-sm text-rose-700">
          No se pudo cargar la comparativa.
        </p>
      )}

      {query.data && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th scope="col" className="px-4 py-2">Métrica</th>
                <th scope="col" className="px-4 py-2">{ranges.a.label}</th>
                <th scope="col" className="px-4 py-2">{ranges.b.label}</th>
                <th scope="col" className="px-4 py-2 text-right">Δ%</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              {buildRows(query.data.a, query.data.b).map((row) => (
                <tr key={row.label} className="border-b border-slate-100 last:border-b-0">
                  <th scope="row" className="px-4 py-2 font-medium text-slate-900">
                    {row.label}
                  </th>
                  <td className="px-4 py-2">{row.fmt(row.a)}</td>
                  <td className="px-4 py-2">{row.fmt(row.b)}</td>
                  <td className={`px-4 py-2 text-right ${deltaClass(row.a, row.b)}`}>
                    {formatDelta(row.a, row.b)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Filas
// ---------------------------------------------------------------------------

interface Row {
  label: string;
  a: number;
  b: number;
  fmt: (n: number) => string;
}

function buildRows(a: PeriodTotals, b: PeriodTotals): Row[] {
  const num = (n: number) => Math.round(n).toString();
  const km = (m: number) => `${(m / 1000).toFixed(1)} km`;
  const hrs = (n: number) => `${n.toFixed(1)} h`;
  return [
    { label: 'Sesiones', a: a.sessions, b: b.sessions, fmt: num },
    { label: 'Horas totales', a: a.hours, b: b.hours, fmt: hrs },
    { label: 'TSS total', a: a.tss, b: b.tss, fmt: num },
    { label: 'Distancia natación', a: a.distanceSwimM, b: b.distanceSwimM, fmt: km },
    { label: 'Distancia ciclismo', a: a.distanceBikeM, b: b.distanceBikeM, fmt: km },
    { label: 'Distancia carrera', a: a.distanceRunM, b: b.distanceRunM, fmt: km },
    { label: 'Distancia total (s+b+r)', a: a.distanceTotalM, b: b.distanceTotalM, fmt: km },
    { label: 'Elevación acumulada (m)', a: a.elevationGainM, b: b.elevationGainM, fmt: num },
    { label: 'Calorías', a: a.calories, b: b.calories, fmt: num },
  ];
}

function formatDelta(a: number, b: number): string {
  if (b === 0) {
    if (a === 0) return '—';
    return '+∞%';
  }
  const delta = ((a - b) / b) * 100;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)}%`;
}

function deltaClass(a: number, b: number): string {
  if (b === 0) return a > 0 ? 'text-emerald-700' : 'text-slate-500';
  if (a === b) return 'text-slate-500';
  return a > b ? 'text-emerald-700' : 'text-rose-700';
}

// ---------------------------------------------------------------------------
// Rangos
// ---------------------------------------------------------------------------

function buildRanges(preset: PresetId): RangePair {
  const now = new Date();
  if (preset === 'month') {
    const startThis = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const endThis = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)) ;
    endThis.setUTCMilliseconds(endThis.getUTCMilliseconds() - 1);
    const startPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const endPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    endPrev.setUTCMilliseconds(endPrev.getUTCMilliseconds() - 1);
    return {
      a: { fromISO: startThis.toISOString(), toISO: endThis.toISOString(), label: monthLabel(startThis) },
      b: { fromISO: startPrev.toISOString(), toISO: endPrev.toISOString(), label: monthLabel(startPrev) },
    };
  }
  if (preset === 'year') {
    const startThis = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    const endThis = new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1));
    endThis.setUTCMilliseconds(endThis.getUTCMilliseconds() - 1);
    const startPrev = new Date(Date.UTC(now.getUTCFullYear() - 1, 0, 1));
    const endPrev = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    endPrev.setUTCMilliseconds(endPrev.getUTCMilliseconds() - 1);
    return {
      a: { fromISO: startThis.toISOString(), toISO: endThis.toISOString(), label: `${startThis.getUTCFullYear()}` },
      b: { fromISO: startPrev.toISOString(), toISO: endPrev.toISOString(), label: `${startPrev.getUTCFullYear()}` },
    };
  }
  // week
  const monday = mondayOf(now);
  const sundayEnd = addDays(monday, 7);
  sundayEnd.setUTCMilliseconds(sundayEnd.getUTCMilliseconds() - 1);
  const prevMonday = addDays(monday, -7);
  const prevSundayEnd = addDays(monday, 0);
  prevSundayEnd.setUTCMilliseconds(prevSundayEnd.getUTCMilliseconds() - 1);
  return {
    a: {
      fromISO: monday.toISOString(),
      toISO: sundayEnd.toISOString(),
      label: `Sem actual (${formatDay(monday)})`,
    },
    b: {
      fromISO: prevMonday.toISOString(),
      toISO: prevSundayEnd.toISOString(),
      label: `Sem anterior (${formatDay(prevMonday)})`,
    },
  };
}

function mondayOf(date: Date): Date {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dow = d.getUTCDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + diff);
  return d;
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date.getTime());
  d.setUTCDate(d.getUTCDate() + n);
  return d;
}

function monthLabel(d: Date): string {
  return new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(d)
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatDay(d: Date): string {
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', timeZone: 'UTC' }).format(d);
}
